import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import PageBody from "../GeneralComponents/PageBody";
import PageBgBlock from "../GeneralComponents/PageBgBlock";
import ListEmpty from "../GeneralComponents/ListEmpty";
import ModalStandardCancel from "../GeneralComponents/ModalStandardCancel";
import GeneralContext from "../GeneralContext/GeneralContext";
import axios from "../../api/axios";

const TIPOS = [
  { valor: "fas", etiqueta: "FAS" },
  { valor: "fus", etiqueta: "FUS" },
  { valor: "contingencias", etiqueta: "Contingencias" },
  { valor: "periodo_gracia", etiqueta: "Período de gracia" },
];

const SEGUNDOS_AUTO = 15;

function numero(valor) {
  if (valor == null) return "—";
  return Number(valor).toLocaleString("es-CL");
}

function corto(texto, largo = 16) {
  if (!texto) return "—";
  if (texto.length <= largo) return texto;
  return `${texto.slice(0, largo)}…`;
}

function describirError(e, porDefecto) {
  const detalle = e?.response?.data?.detail;
  if (typeof detalle === "string") return detalle;
  const status = e?.response?.status;
  if (status === 403) return "No tienes privilegios para administrar los locks de la base de datos.";
  if (status === 404) return "El servicio de administración de locks no está disponible.";
  return porDefecto;
}

function Aviso({ severidad, children }) {
  const estilos = {
    error: { color: "#C62828", borde: "rgba(198,40,40,0.3)", fondo: "rgba(198,40,40,0.08)", icono: <ErrorOutlineIcon fontSize="small" /> },
    advertencia: { color: "#B35300", borde: "rgba(230,81,0,0.3)", fondo: "rgba(230,81,0,0.08)", icono: <WarningAmberIcon fontSize="small" /> },
    ok: { color: "#2E7D32", borde: "rgba(46,125,50,0.3)", fondo: "rgba(46,125,50,0.08)", icono: <CheckCircleOutlineIcon fontSize="small" /> },
  }[severidad];

  return (
    <Paper
      elevation={0}
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: 1.5,
        p: 2,
        mb: 2,
        border: `1px solid ${estilos.borde}`,
        background: estilos.fondo,
      }}
    >
      <Box sx={{ color: estilos.color, display: "flex", pt: "2px" }}>{estilos.icono}</Box>
      <Box sx={{ color: estilos.color }}>{children}</Box>
    </Paper>
  );
}

function EstadoLock({ lock }) {
  if (lock.estado_lock !== "GRANTED") {
    return (
      <Chip
        size="small"
        label="En espera"
        sx={{ color: "#1565c0", background: "rgba(21,101,192,0.12)", fontWeight: 600, borderRadius: 0.5 }}
      />
    );
  }
  if (lock.sospechoso) {
    return (
      <Chip
        size="small"
        label="Tomado (inactivo)"
        sx={{ color: "#B35300", background: "rgba(230,81,0,0.12)", fontWeight: 600, borderRadius: 0.5 }}
      />
    );
  }
  return (
    <Chip
      size="small"
      label="Tomado"
      sx={{ color: "#2E7D32", background: "rgba(46,125,50,0.12)", fontWeight: 600, borderRadius: 0.5 }}
    />
  );
}

export default function LocksAdministracion() {
  const { setPageTitle } = useContext(GeneralContext);

  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState(null);
  const [incluirTransacciones, setIncluirTransacciones] = useState(true);
  const [auto, setAuto] = useState(false);

  const [modo, setModo] = useState("documento");
  const [tipo, setTipo] = useState("contingencias");
  const [rbd, setRbd] = useState("");
  const [nombreLibre, setNombreLibre] = useState("");
  const [previsualizacion, setPrevisualizacion] = useState(null);

  const [confirmacion, setConfirmacion] = useState(null);
  const [ejecutando, setEjecutando] = useState(false);
  const [resultado, setResultado] = useState(null);

  const temporizador = useRef(null);

  useEffect(() => {
    setPageTitle?.("LOCKS DE BASE DE DATOS");
  }, []);

  const consultar = (silencioso = false) => {
    if (!silencioso) setCargando(true);
    axios
      .getLocksBD({ incluir_transacciones: incluirTransacciones })
      .then((res) => {
        setDatos(res?.data ?? null);
        setError(null);
      })
      .catch((e) => {
        setDatos(null);
        setError(describirError(e, "No se pudo consultar el estado de los locks."));
      })
      .finally(() => setCargando(false));
  };

  useEffect(() => {
    consultar();
  }, [incluirTransacciones]);

  useEffect(() => {
    if (temporizador.current) clearInterval(temporizador.current);
    if (auto) {
      temporizador.current = setInterval(() => consultar(true), SEGUNDOS_AUTO * 1000);
    }
    return () => {
      if (temporizador.current) clearInterval(temporizador.current);
    };
  }, [auto, incluirTransacciones]);

  const objetivoManual = useMemo(() => {
    if (modo === "documento") {
      if (!rbd.trim()) return null;
      return { tipo, rbd: rbd.trim() };
    }
    if (!nombreLibre.trim()) return null;
    return { nombre: nombreLibre.trim() };
  }, [modo, tipo, rbd, nombreLibre]);

  useEffect(() => {
    if (!objetivoManual) {
      setPrevisualizacion(null);
      return;
    }
    const id = setTimeout(() => {
      axios
        .getNombreLockBD(objetivoManual)
        .then((res) => setPrevisualizacion(res?.data ?? null))
        .catch(() => setPrevisualizacion(null));
    }, 350);
    return () => clearTimeout(id);
  }, [objetivoManual]);

  const ejecutarLiberacion = (objetivo) => {
    setEjecutando(true);
    setResultado(null);
    axios
      .liberarLockBD(objetivo)
      .then((res) => {
        setResultado({ tipo: "lock", datos: res?.data ?? null, error: null });
        setError(null);
      })
      .catch((e) => {
        setResultado({
          tipo: "lock",
          datos: null,
          error: describirError(e, "No se pudo liberar el lock."),
        });
      })
      .finally(() => {
        setEjecutando(false);
        consultar(true);
      });
  };

  const ejecutarMuerte = (conexionId) => {
    setEjecutando(true);
    setResultado(null);
    axios
      .matarConexionBD({ conexion_id: conexionId })
      .then((res) => {
        setResultado({ tipo: "conexion", datos: res?.data ?? null, error: null });
        setError(null);
      })
      .catch((e) => {
        setResultado({
          tipo: "conexion",
          datos: null,
          error: describirError(e, "No se pudo terminar la conexión."),
        });
      })
      .finally(() => {
        setEjecutando(false);
        consultar(true);
      });
  };

  const confirmar = () => {
    const pendiente = confirmacion;
    setConfirmacion(null);
    if (!pendiente) return;
    if (pendiente.accion === "liberar") ejecutarLiberacion(pendiente.objetivo);
    else ejecutarMuerte(pendiente.conexionId);
  };

  const locks = datos?.locks ?? [];
  const transacciones = datos?.transacciones ?? [];
  const esperas = datos?.esperas ?? [];
  const totales = datos?.totales ?? {};
  const advertencias = datos?.advertencias ?? [];

  return (
    <PageBody>
      <Box width="100%">
        <Typography variant="h1" sx={{ textAlign: "center", p: "0.6rem" }}>
          LOCKS DE BASE DE DATOS
        </Typography>
        <Typography variant="body2" sx={{ textAlign: "center", mb: 3 }}>
          Locks con nombre (GET_LOCK) que la API toma al crear FAS, FUS y contingencias, y
          transacciones InnoDB abiertas. Un lock queda colgado cuando la conexión que lo tomó
          no alcanzó a liberarlo; liberarlo termina esa conexión.
        </Typography>

        <Box
          sx={{
            mb: 3,
            p: 2.5,
            border: "1px solid rgba(0,0,0,0.15)",
            borderRadius: 1,
            background: "#fff",
            display: "flex",
            alignItems: "flex-start",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Box sx={{ flex: "0 0 200px" }}>
            <Typography variant="body1" sx={{ mb: 0.75, fontWeight: 500 }}>
              Buscar por:
            </Typography>
            <TextField
              select
              size="small"
              fullWidth
              value={modo}
              disabled={ejecutando}
              onChange={(e) => setModo(e.target.value)}
            >
              <MenuItem value="documento">Tipo de documento y RBD</MenuItem>
              <MenuItem value="nombre">Nombre del lock</MenuItem>
            </TextField>
          </Box>

          {modo === "documento" ? (
            <>
              <Box sx={{ flex: "0 0 220px" }}>
                <Typography variant="body1" sx={{ mb: 0.75, fontWeight: 500 }}>
                  Documento:
                </Typography>
                <TextField
                  select
                  size="small"
                  fullWidth
                  value={tipo}
                  disabled={ejecutando}
                  onChange={(e) => setTipo(e.target.value)}
                >
                  {TIPOS.map((t) => (
                    <MenuItem key={t.valor} value={t.valor}>
                      {t.etiqueta}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>

              <Box sx={{ flex: "0 0 180px" }}>
                <Typography variant="body1" sx={{ mb: 0.75, fontWeight: 500 }}>
                  RBD:
                </Typography>
                <TextField
                  size="small"
                  fullWidth
                  value={rbd}
                  disabled={ejecutando}
                  placeholder="12345"
                  onChange={(e) => setRbd(e.target.value)}
                />
              </Box>
            </>
          ) : (
            <Box sx={{ flex: "1 1 420px", minWidth: 320 }}>
              <Typography variant="body1" sx={{ mb: 0.75, fontWeight: 500 }}>
                Nombre o clave del lock:
              </Typography>
              <TextField
                size="small"
                fullWidth
                value={nombreLibre}
                disabled={ejecutando}
                placeholder="create:contingencias:12345 o el hash de 64 caracteres"
                onChange={(e) => setNombreLibre(e.target.value)}
              />
            </Box>
          )}

          <Box sx={{ alignSelf: "flex-end", display: "flex", gap: 1 }}>
            <Button
              variant="contained"
              startIcon={ejecutando ? <CircularProgress size={16} /> : <LockOpenIcon />}
              disabled={ejecutando || !objetivoManual}
              onClick={() =>
                setConfirmacion({
                  accion: "liberar",
                  objetivo: objetivoManual,
                  titulo: "Liberar lock",
                  pregunta: previsualizacion?.clave ?? previsualizacion?.nombre ?? "",
                  mensaje:
                    "Si el lock está tomado por otra conexión, esa conexión se terminará para " +
                    "liberarlo. Si no está tomado, sólo se verifica que el nombre quede disponible.",
                })
              }
              sx={{
                background: "#C8D8F0",
                color: "#1565c0",
                boxShadow: "none",
                textTransform: "none",
                "&:hover": { background: "#b3c8e8", boxShadow: "none" },
              }}
            >
              Liberar
            </Button>
            <Button
              startIcon={<RefreshIcon />}
              disabled={cargando || ejecutando}
              onClick={() => consultar()}
              sx={{ textTransform: "none" }}
            >
              Actualizar
            </Button>
          </Box>

          <Box sx={{ alignSelf: "flex-end", display: "flex", gap: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={incluirTransacciones}
                  disabled={ejecutando}
                  onChange={(e) => setIncluirTransacciones(e.target.checked)}
                />
              }
              label={<Typography variant="body2">Incluir transacciones</Typography>}
            />
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={auto}
                  onChange={(e) => setAuto(e.target.checked)}
                />
              }
              label={<Typography variant="body2">Refrescar cada {SEGUNDOS_AUTO}s</Typography>}
            />
          </Box>

          <Box sx={{ flexBasis: "100%" }}>
            <Typography variant="caption" sx={{ color: "rgba(0,0,0,0.6)" }}>
              {previsualizacion
                ? `Se actuará sobre el lock ${previsualizacion.nombre}${
                    previsualizacion.clave ? ` (${previsualizacion.clave})` : ""
                  }. Funciona aunque el lock no aparezca en la lista.`
                : "Indica el documento y el RBD, o el nombre del lock, para intentar liberarlo aunque no esté listado."}
            </Typography>
          </Box>
        </Box>

        {error && <Aviso severidad="error">{error}</Aviso>}

        {advertencias.map((mensaje, i) => (
          <Aviso key={i} severidad="advertencia">
            <Typography variant="body2">{mensaje}</Typography>
          </Aviso>
        ))}

        {resultado?.error && <Aviso severidad="error">{resultado.error}</Aviso>}

        {resultado?.datos && (
          <Aviso
            severidad={
              resultado.tipo === "lock"
                ? resultado.datos.liberado
                  ? "ok"
                  : "advertencia"
                : resultado.datos.terminada
                ? "ok"
                : "advertencia"
            }
          >
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {resultado.tipo === "lock"
                ? !resultado.datos.estaba_tomado
                  ? `El lock ${corto(resultado.datos.nombre, 24)} no estaba tomado`
                  : resultado.datos.liberado
                  ? `Lock ${corto(resultado.datos.nombre, 24)} liberado`
                  : `El lock ${corto(resultado.datos.nombre, 24)} sigue tomado`
                : resultado.datos.terminada
                ? `Conexión ${resultado.datos.conexion_id} terminada`
                : `No se pudo terminar la conexión ${resultado.datos.conexion_id}`}
            </Typography>
            {(resultado.datos.acciones ?? []).map((accion, i) => (
              <Typography key={i} variant="body2">
                · {accion}
              </Typography>
            ))}
          </Aviso>
        )}

        <PageBgBlock pad="0">
          <Box sx={{ px: 2, pt: 2, pb: 1, display: "flex", alignItems: "baseline", gap: 2, flexWrap: "wrap" }}>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              Locks con nombre
            </Typography>
            <Typography variant="body2">
              {numero(totales.otorgados)} tomados · {numero(totales.en_espera)} en espera ·{" "}
              {numero(totales.sospechosos)} inactivos
              {datos?.servidor ? ` · conexión actual ${datos.servidor.conexion_id}` : ""}
            </Typography>
          </Box>

          {locks.length === 0 ? (
            <Box sx={{ p: 2 }}>
              <ListEmpty msg="No hay locks con nombre tomados en este momento." />
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Lock</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Documento</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Estado</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">
                      Conexión
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Usuario</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Comando</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">
                      Segundos
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">
                      Acción
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {locks.map((lock, i) => (
                    <TableRow key={`${lock.nombre}-${lock.conexion_id}-${i}`} hover>
                      <TableCell>
                        <Tooltip title={lock.nombre ?? ""}>
                          <span>{corto(lock.nombre, 18)}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>{lock.clave ?? "—"}</TableCell>
                      <TableCell>
                        <EstadoLock lock={lock} />
                      </TableCell>
                      <TableCell align="right">{lock.conexion_id ?? "—"}</TableCell>
                      <TableCell>
                        {lock.usuario ?? "—"}
                        {lock.propio ? " (esta sesión)" : ""}
                      </TableCell>
                      <TableCell>
                        {lock.comando ?? "—"}
                        {lock.estado_conexion ? ` · ${lock.estado_conexion}` : ""}
                      </TableCell>
                      <TableCell align="right">{numero(lock.segundos)}</TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          startIcon={<LockOpenIcon />}
                          disabled={ejecutando || lock.estado_lock !== "GRANTED"}
                          onClick={() =>
                            setConfirmacion({
                              accion: "liberar",
                              objetivo: { nombre: lock.nombre, hashear: false },
                              titulo: "Liberar lock",
                              pregunta: lock.clave ?? corto(lock.nombre, 24),
                              mensaje: `Se terminará la conexión ${lock.conexion_id} para liberar el lock.`,
                            })
                          }
                          sx={{ textTransform: "none" }}
                        >
                          Liberar
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </PageBgBlock>

        {incluirTransacciones && (
          <Box sx={{ mt: 3 }}>
            <PageBgBlock pad="0">
              <Box sx={{ px: 2, pt: 2, pb: 1, display: "flex", alignItems: "baseline", gap: 2 }}>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  Transacciones InnoDB abiertas
                </Typography>
                <Typography variant="body2">
                  {numero(totales.transacciones)} abiertas · {numero(totales.esperas)} esperando
                </Typography>
              </Box>

              {transacciones.length === 0 ? (
                <Box sx={{ p: 2 }}>
                  <ListEmpty msg="No hay transacciones InnoDB abiertas." />
                </Box>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Transacción</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Estado</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="right">
                          Conexión
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="right">
                          Segundos
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="right">
                          Filas bloqueadas
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Consulta</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="right">
                          Acción
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {transacciones.map((trx) => (
                        <TableRow key={trx.trx_id} hover>
                          <TableCell>{trx.trx_id}</TableCell>
                          <TableCell>{trx.estado}</TableCell>
                          <TableCell align="right">{trx.conexion_id ?? "—"}</TableCell>
                          <TableCell align="right">{numero(trx.segundos)}</TableCell>
                          <TableCell align="right">{numero(trx.filas_bloqueadas)}</TableCell>
                          <TableCell sx={{ maxWidth: 320 }}>
                            <Tooltip title={trx.consulta ?? ""}>
                              <Typography variant="body2" noWrap>
                                {trx.consulta ?? "—"}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                          <TableCell align="right">
                            <Button
                              size="small"
                              startIcon={<PowerSettingsNewIcon />}
                              disabled={ejecutando || trx.conexion_id == null}
                              onClick={() =>
                                setConfirmacion({
                                  accion: "matar",
                                  conexionId: trx.conexion_id,
                                  titulo: "Terminar conexión",
                                  pregunta: `Conexión ${trx.conexion_id}`,
                                  mensaje:
                                    "Se terminará la conexión y su transacción abierta hará rollback. " +
                                    "El trabajo no confirmado se pierde.",
                                })
                              }
                              sx={{ textTransform: "none" }}
                            >
                              Terminar
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {esperas.length > 0 && (
                <TableContainer sx={{ mt: 1 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }} colSpan={5}>
                          Bloqueos entre transacciones
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }} align="right">
                          Conexión bloqueada
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="right">
                          Segundos esperando
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="right">
                          Conexión bloqueante
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Consulta bloqueante</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="right">
                          Acción
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {esperas.map((espera, i) => (
                        <TableRow key={i} hover>
                          <TableCell align="right">{espera.conexion_bloqueada ?? "—"}</TableCell>
                          <TableCell align="right">{numero(espera.segundos_espera)}</TableCell>
                          <TableCell align="right">{espera.conexion_bloqueante ?? "—"}</TableCell>
                          <TableCell sx={{ maxWidth: 320 }}>
                            <Tooltip title={espera.consulta_bloqueante ?? ""}>
                              <Typography variant="body2" noWrap>
                                {espera.consulta_bloqueante ?? "—"}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                          <TableCell align="right">
                            <Button
                              size="small"
                              startIcon={<PowerSettingsNewIcon />}
                              disabled={ejecutando || espera.conexion_bloqueante == null}
                              onClick={() =>
                                setConfirmacion({
                                  accion: "matar",
                                  conexionId: espera.conexion_bloqueante,
                                  titulo: "Terminar conexión bloqueante",
                                  pregunta: `Conexión ${espera.conexion_bloqueante}`,
                                  mensaje:
                                    "Se terminará la conexión que mantiene el bloqueo y su transacción " +
                                    "hará rollback.",
                                })
                              }
                              sx={{ textTransform: "none" }}
                            >
                              Terminar
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </PageBgBlock>
          </Box>
        )}

        <ModalStandardCancel
          open={Boolean(confirmacion)}
          error
          onClose={() => setConfirmacion(null)}
          onClick={confirmar}
          btnText={confirmacion?.accion === "liberar" ? "Liberar" : "Terminar"}
          title={confirmacion?.titulo ?? ""}
          qst={confirmacion?.pregunta ?? ""}
          msg={confirmacion?.mensaje ?? ""}
        />
      </Box>
    </PageBody>
  );
}
